"use client";

import React from "react";
import Link from "next/link";
import { SectionHeader } from "fansunited-sports-ui";
import { TeamLogo } from "@/components/ui/TeamLogo";
import { usePostData } from "../../context/PostDataContext";
import { useLabels } from "@/lib/labels/LabelsProvider";
import type { Labels } from "@/lib/labels/types";
import { useNavigation } from "@/lib/navigation/NavigationProvider";
import type { UrlBuilder } from "@/lib/navigation/createUrlBuilder";
import { translatePosition } from "@/utilities/mappers/squad";
import { SITE_TYPE_NEWS_BLOG, SITE_TYPE_BASIC } from "@/config/siteTypes";
import type { RelatedEntity } from "../../context/types";

export type RelatedEntitiesVariant = "default" | "compact" | "minimal";

export interface RelatedEntitiesProps {
    /** Visual variant of the section */
    variant?: RelatedEntitiesVariant;
    /** Custom title override */
    title?: string;
    /** Maximum number of entities to display. When omitted, all entities are shown. */
    maxItems?: number;
    /** Custom container className */
    className?: string;
    /** Number of columns (1 for single column, undefined for responsive grid) */
    columns?: 1 | 2 | 3;
    /** Localized label for "About" section */
    aboutLabel: string;
}

/**
 * Helper to get entity display info (imageUrl, name, subtitle, imageBorderRadius, href)
 */
function getEntityDisplayInfo(
    entity: RelatedEntity,
    builder: UrlBuilder,
    siteType: string,
    playerPositionLabels?: Labels["teamPage"]["playerPositions"]
): {
    imageUrl: string;
    image?: React.ReactNode;
    name: string;
    subtitle: string;
    imageBorderRadius: "none" | "full" | "md" | "lg";
    href: string | null;
} {
    switch (entity.type) {
        case "team": {
            const team = entity.data;
            return {
                imageUrl: team.assets?.logo || "",
                image: (
                    <TeamLogo
                        src={team.assets?.logo}
                        alt={team.name}
                        size="sm"
                        countryCode={team.country?.code}
                        countryFlag={team.country?.flag}
                        primaryColor={team.metadata?.branding?.primaryColor}
                        secondaryColor={team.metadata?.branding?.secondaryColor}
                    />
                ),
                name: team.name,
                subtitle: team.country?.name || "",
                imageBorderRadius: "none",
                href: builder.team(entity.entityId),
            };
        }
        case "player": {
            const player = entity.data;
            // Get the player's club team (not national team)
            const clubTeam = player.teams?.find((team) => team.type === "club");
            const currentClub = clubTeam?.name;
            // Translate position to localized label
            const localizedPosition = translatePosition(player.position, playerPositionLabels);
            // Build subtitle: "Club • Position" or just one if the other is missing
            const subtitleParts = [currentClub, localizedPosition].filter(Boolean);
            const subtitle = subtitleParts.join(" • ");
            const playerImageUrl = player.assets?.photo || player.assets?.photoTransparent || "";
            return {
                imageUrl: playerImageUrl,
                image: playerImageUrl ? (
                    <img src={playerImageUrl} alt={player.name} className="w-full h-full object-cover" loading="lazy" />
                ) : undefined,
                name: player.name,
                subtitle,
                imageBorderRadius: "full",
                href: builder.player(entity.entityId),
            };
        }
        case "competition": {
            const competition = entity.data;
            // Only news-blog sites have competition detail pages
            const href = siteType === SITE_TYPE_NEWS_BLOG ? builder.footballCompetition(entity.entityId) : null;
            const competitionLogo = competition.assets?.logo || "";
            return {
                imageUrl: competitionLogo,
                image: competitionLogo ? (
                    <img
                        src={competitionLogo}
                        alt={competition.name}
                        className="w-full h-full object-contain"
                        loading="lazy"
                    />
                ) : undefined,
                name: competition.name,
                subtitle: competition.country?.name || "",
                imageBorderRadius: "none",
                href,
            };
        }
        default:
            return { imageUrl: "", name: "", subtitle: "", imageBorderRadius: "none", href: null };
    }
}

/**
 * RelatedEntities - Displays related entities (teams, players, competitions) from the post context.
 * - Teams, Players, Competitions: Combined grid of ProfileHeader cards with links
 * - Matches are handled separately by the RelatedMatches component
 */
export function RelatedEntities({
    variant = "default",
    title,
    maxItems,
    className = "mt-8 pt-6",
    columns,
    aboutLabel,
}: RelatedEntitiesProps) {
    const { relatedEntities, hasRelatedEntities, getEntitiesByType, siteType } = usePostData();
    const labels = useLabels();
    const nav = useNavigation();

    // Basic sites have no sport entity pages — never render the related entities sidebar.
    if (siteType === SITE_TYPE_BASIC) return null;

    // Don't render if no related entities
    if (!hasRelatedEntities || relatedEntities.length === 0) {
        return null;
    }

    // Get entities by type (excluding matches - they're handled by RelatedMatches)
    const teams = getEntitiesByType("team");
    const players = getEntitiesByType("player");
    const competitions = getEntitiesByType("competition");

    // Combine all profile entities into one grid (optionally capped by maxItems)
    const allProfileEntities = [...teams, ...players, ...competitions];
    const profileEntities = maxItems != null ? allProfileEntities.slice(0, maxItems) : allProfileEntities;

    const sectionTitle = title ?? aboutLabel;

    // Don't render if no entities to display
    if (profileEntities.length === 0) {
        return null;
    }

    return (
        <section className={className}>
            <SectionHeader
                entity={{
                    name: sectionTitle,
                    styles: {
                        typography: {
                            name: "text-sm uppercase font-semibold text-foreground",
                        },
                    },
                }}
                styles={{ container: "mb-4" }}
            />

            <EntityGrid
                entities={profileEntities}
                variant={variant}
                builder={nav}
                siteType={siteType}
                playerPositionLabels={labels.teamPage.playerPositions}
                columns={columns}
            />
        </section>
    );
}

// Entity Grid Sub-component (for teams, players, competitions)
interface EntityGridProps {
    entities: RelatedEntity[];
    variant: RelatedEntitiesVariant;
    builder: UrlBuilder;
    siteType: string;
    playerPositionLabels: Labels["teamPage"]["playerPositions"];
    columns?: 1 | 2 | 3;
}

function EntityGrid({ entities, builder, siteType, playerPositionLabels }: EntityGridProps) {
    return (
        <div className="flex flex-wrap gap-2">
            {entities.map((entity) => {
                const { image, name, imageBorderRadius, href } = getEntityDisplayInfo(
                    entity,
                    builder,
                    siteType,
                    playerPositionLabels
                );

                // Rounded shapes need overflow-hidden to clip the image; "none" leaves crests/logos untrimmed
                const wrapperShapeClass =
                    imageBorderRadius === "full"
                        ? "rounded-full overflow-hidden"
                        : imageBorderRadius === "lg"
                          ? "rounded-lg overflow-hidden"
                          : imageBorderRadius === "md"
                            ? "rounded-md overflow-hidden"
                            : "";

                const pill = (
                    <span className="inline-flex items-center gap-2 bg-muted hover:bg-muted/80 rounded-full pl-1.5 pr-4 py-1.5 transition-colors">
                        {image && (
                            <span className={`flex items-center justify-center w-7 h-7 ${wrapperShapeClass}`.trim()}>
                                {image}
                            </span>
                        )}
                        <span className="text-sm font-medium text-foreground">{name}</span>
                    </span>
                );

                if (href) {
                    return (
                        <Link prefetch={false} key={entity.entityId} href={href} className="inline-block">
                            {pill}
                        </Link>
                    );
                }

                return (
                    <span key={entity.entityId} className="inline-block">
                        {pill}
                    </span>
                );
            })}
        </div>
    );
}
